import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import {
  getCookieConsent,
  setCookieConsent,
} from "@/lib/cookie-consent";

/**
 * Fixed-bottom cookie consent banner.
 *
 * Renders only when the user hasn't made a choice yet. "Essential only"
 * is the primary, privacy-preserving default - styled as the main volt
 * CTA so the user can dismiss without being nudged to accept tracking.
 * "Accept all" is the secondary outline button.
 *
 * Consent is persisted via the cookie-consent module; Sentry reads the
 * value at init time to decide whether to enable Session Replay, and we
 * re-init once the user makes a choice so Replay can either start or
 * stay off without a page reload.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer first render so the banner doesn't flash on routes that
    // redirect away (e.g. the /not-found page). If no choice stored,
    // show it.
    setVisible(getCookieConsent() === null);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setCookieConsent("accepted");
    setVisible(false);
  };
  const essentialOnly = () => {
    setCookieConsent("essential");
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-2xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur-md sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:p-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
        <div className="flex shrink-0 items-start gap-3">
          <div
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-volt-subtle text-volt"
          >
            <Cookie className="h-4 w-4" />
          </div>
          <div className="sm:hidden">
            <p
              id="cookie-banner-title-mobile"
              className="text-sm font-semibold"
            >
              Cookies
            </p>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p
            id="cookie-banner-title"
            className="hidden text-sm font-semibold sm:block"
          >
            We use cookies to keep Whistll running.
          </p>
          <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
            Essential cookies keep you signed in and power core features.
            Optional analytics help us improve the app.{" "}
            <Link
              to="/cookie-policy"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Cookie policy
            </Link>
            .
          </p>
        </div>

        <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={accept}
            className="h-10 rounded-md border border-input bg-background px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={essentialOnly}
            className="h-10 rounded-md bg-volt px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-volt-strong"
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
}
